import React, { useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { useLocation } from 'react-router-dom'
import qs from 'query-string'
import { GET_COACH_GROUP_EVENTS } from './query'
import { DateTime } from 'luxon'
import Error from 'components/error'
import Typography from '@material-ui/core/Typography'
import EventsSlider from './events-slider'
import LinearProgress from '@material-ui/core/LinearProgress'
import pipe from 'lodash/fp/pipe'
import flatMap from 'lodash/fp/flatMap'
import uniqBy from 'lodash/fp/uniqBy'
import GroupEventCard from 'components/group-event-card/group-event-card'
import { isRegistrationNotYetOpen } from 'pages/things-to-do/events/register/registration-cta-utils'

export const isListedUpcomingForViewer = (ge, isOwnSchedule) => {
  if (ge.unlisted) return false
  if (!ge.registrationClosed) return true
  return isOwnSchedule && isRegistrationNotYetOpen(ge)
}

export const isClosedPastForOwner = ge =>
  ge.registrationClosed && !isRegistrationNotYetOpen(ge)

const filterGroupEventsByLocationUpcoming = (
  groupEvents,
  selectedFacility,
  isOwnSchedule,
) => {
  let filteredGE = []
  if (!groupEvents) return filteredGE

  const listedGE = groupEvents.filter(ge =>
    isListedUpcomingForViewer(ge, isOwnSchedule),
  )

  if (selectedFacility === 'all') return listedGE
  filteredGE = listedGE.filter(ge => ge.coachFacility.id === selectedFacility)
  return filteredGE
}

const filterGroupEventsByLocationPast = (groupEvents, selectedFacility) => {
  let filteredGE = []
  if (!groupEvents) return filteredGE

  // Coaches should only see their own events whose registration is closed.
  if (selectedFacility === 'all') {
    return groupEvents.filter(ge => isClosedPastForOwner(ge))
  }

  filteredGE = groupEvents.filter(
    ge => ge.coachFacility.id === selectedFacility && isClosedPastForOwner(ge),
  )
  return filteredGE
}

const CoachGroupEvents = ({
  coach,
  selectedFacility,
  setGroupEventsLocations,
  classes,
  coachIsViewingOwnSchedule,
}) => {
  const today = DateTime.local()
    .startOf('day')
    .toISO()

  const upcomingStartDateTime = {
    from: today,
    to: DateTime.local()
      .plus({ years: 1 })
      .toISODate(),
  }

  const pastStartDateTime = {
    from: DateTime.local()
      .minus({ months: 2 })
      .startOf('day')
      .toISO(),
    to: DateTime.local()
      .plus({ week: 1 })
      .toISODate(),
  }

  const {
    data: upcomingData,
    loading: upcomingLoading,
    error: upcomingError,
  } = useQuery(GET_COACH_GROUP_EVENTS, {
    variables: {
      startDateTime: upcomingStartDateTime,
      coachId: coach.id,
      includeCanceled: false,
      limit: 50,
    },
  })

  const { data: pastData, loading: pastLoading, error: pastError } = useQuery(
    GET_COACH_GROUP_EVENTS,
    {
      variables: {
        startDateTime: pastStartDateTime,
        coachId: coach.id,
        includeCanceled: false,
        limit: 50,
      },
      skip: !coachIsViewingOwnSchedule,
    },
  )

  const location = useLocation()
  const { student } = qs.parse(location.search)
  const linkUpcomingToRegister = coachIsViewingOwnSchedule && Boolean(student)

  const upcomingGroupEvents = upcomingData?.groupEvents
  const pastGroupEvents = pastData?.groupEvents

  const upcomingEvents = filterGroupEventsByLocationUpcoming(
    upcomingGroupEvents,
    selectedFacility,
    coachIsViewingOwnSchedule,
  )
  const pastEvents = filterGroupEventsByLocationPast(
    pastGroupEvents,
    selectedFacility,
  )

  useEffect(() => {
    const upcomingEventsForLocations = (upcomingGroupEvents || []).filter(ge =>
      isListedUpcomingForViewer(ge, coachIsViewingOwnSchedule),
    )
    const pastEventsForLocations = (pastGroupEvents || []).filter(ge =>
      isClosedPastForOwner(ge),
    )

    const groupEventsLocations = pipe(
      flatMap(ge => ge.coachFacility),
      uniqBy('id'),
    )([...upcomingEventsForLocations, ...pastEventsForLocations])

    setGroupEventsLocations(groupEventsLocations)
  }, [
    upcomingGroupEvents,
    pastGroupEvents,
    setGroupEventsLocations,
    coachIsViewingOwnSchedule,
  ])

  if (upcomingLoading || pastLoading) {
    return <LinearProgress color="secondary" />
  }
  if (upcomingError) return <Error error={upcomingError} />
  if (pastError) return <Error error={pastError} />

  const shouldShowPast = coachIsViewingOwnSchedule && pastEvents.length > 0

  return upcomingEvents.length > 0 || shouldShowPast ? (
    <>
      {upcomingEvents.length > 0 ? (
        <Typography variant="h4" className={classes.eventsTitle}>
          Upcoming Events
        </Typography>
      ) : null}

      {upcomingEvents.length > 0 ? (
        <EventsSlider
          events={upcomingEvents}
          renderEvent={event => (
            <GroupEventCard
              key={event.slug}
              event={event}
              coach={coach}
              linkToRegister={linkUpcomingToRegister}
            />
          )}
        />
      ) : null}

      {shouldShowPast ? (
        <>
          <Typography variant="h4">Registration Closed Events</Typography>
          <EventsSlider
            events={pastEvents}
            renderEvent={event => (
              <GroupEventCard
                key={event.slug}
                event={event}
                coach={coach}
                linkToRegister
              />
            )}
          />
        </>
      ) : null}
    </>
  ) : null
}

export default CoachGroupEvents
